import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { KeyRound, Save, Trash2 } from "lucide-react";

const DEFAULT_MODELS = {
    openai: "gpt-4o-mini",
    anthropic: "claude-3-5-sonnet-latest",
    deepseek: "deepseek-v4-pro",
    ollama: "llama3.1",
};

const DEFAULT_BASE_URLS = {
    openai: "https://api.openai.com/v1",
    deepseek: "https://api.deepseek.com",
    ollama: "http://localhost:11434/v1",
};

export default function AiSettings() {
    const [existing, setExisting] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        provider: "deepseek",
        model: DEFAULT_MODELS.deepseek,
        api_key: "",
        base_url: DEFAULT_BASE_URLS.deepseek,
    });

    const load = async () => {
        const r = await api.get("/me/ai-credentials");
        setExisting(r.data);
        if (r.data) {
            setForm({
                provider: r.data.provider,
                model: r.data.model,
                api_key: "",
                base_url: r.data.base_url || DEFAULT_BASE_URLS[r.data.provider] || "",
            });
        }
    };

    useEffect(() => { load().catch(() => { }); }, []);

    const changeProvider = (provider) => {
        setForm({
            ...form,
            provider,
            model: DEFAULT_MODELS[provider],
            base_url: DEFAULT_BASE_URLS[provider] || "",
            api_key: "",
        });
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.put("/me/ai-credentials", {
                ...form,
                base_url: form.base_url || null,
            });
            setExisting(r.data);
            setForm({ ...form, api_key: "" });
            toast.success("AI credentials saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unable to save credentials");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete("/me/ai-credentials");
            setExisting(null);
            setForm({
                provider: "deepseek",
                model: DEFAULT_MODELS.deepseek,
                api_key: "",
                base_url: DEFAULT_BASE_URLS.deepseek,
            });
            toast.success("Using platform AI provider");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unable to remove credentials");
        } finally {
            setBusy(false);
        }
    };

    const needsBaseUrl = ["openai", "deepseek", "ollama"].includes(form.provider);

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <span className="overline text-primary">AI Credentials</span>
            <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-heading font-black text-4xl tracking-tighter">Bring your own compute key</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                        Prepcheck will use your saved provider first for question generation and analytics, then fall back to the Illume AI server provider.
                    </p>
                </div>
                <div className="border border-border rounded-sm px-4 py-3 font-mono text-xs">
                    {existing ? `Configured: ${existing.provider} | ${existing.api_key_masked}` : "Using platform provider"}
                </div>
            </div>

            <form onSubmit={save} className="mt-8 border border-border rounded-sm p-6 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label className="overline">Provider</Label>
                        <Select value={form.provider} onValueChange={changeProvider}>
                            <SelectTrigger data-testid="ai-provider-select" className="rounded-sm mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="deepseek">DeepSeek</SelectItem>
                                <SelectItem value="openai">OpenAI</SelectItem>
                                <SelectItem value="anthropic">Anthropic Claude</SelectItem>
                                <SelectItem value="ollama">Ollama</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="overline">Model</Label>
                        <Input data-testid="ai-model-input" className="rounded-sm mt-2 font-mono" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} required />
                    </div>
                </div>

                <div>
                    <Label className="overline">API key</Label>
                    <Input data-testid="ai-key-input" type="password" className="rounded-sm mt-2 font-mono" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={form.provider === "ollama" ? "Optional for local Ollama" : "Paste provider API key"} />
                    {existing && <p className="mt-2 text-xs text-muted-foreground">Saved secrets are encrypted and never shown again. Enter a new key to replace the current one.</p>}
                </div>

                {needsBaseUrl && (
                    <div>
                        <Label className="overline">Base URL</Label>
                        <Input data-testid="ai-base-url-input" className="rounded-sm mt-2 font-mono" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder={DEFAULT_BASE_URLS[form.provider] || ""} />
                    </div>
                )}

                <div className="flex flex-wrap gap-2">
                    <Button data-testid="ai-save-btn" disabled={busy} className="rounded-sm bg-primary hover:bg-primary/90 btn-press">
                        <Save className="w-4 h-4 mr-2" /> {busy ? "Saving..." : "Save credentials"}
                    </Button>
                    {existing && (
                        <Button data-testid="ai-delete-btn" type="button" variant="outline" disabled={busy} onClick={remove} className="rounded-sm btn-press">
                            <Trash2 className="w-4 h-4 mr-2" /> Use platform provider
                        </Button>
                    )}
                </div>
            </form>

            <div className="mt-6 border border-border rounded-sm p-5 text-sm text-muted-foreground">
                <KeyRound className="w-5 h-5 text-primary mb-3" />
                Ollama base URLs must be reachable from the Prepcheck server. For a Linode deployment, run Ollama on the same server or expose a private network endpoint.
            </div>
        </div>
    );
}
